
import React, { useState, useEffect, useMemo } from 'react';
import { Check, ShieldCheck, Loader2, Search, X, Globe, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { LeagueDB, searchLeagues, fetchLeaguesByIds } from '../../services/footballService';
import { Language } from '../../types';
import { t } from '../../i18n';
import { useDebounce } from '../../hooks/useDebounce';
import { getLeagueName, LEAGUE_TRANSLATIONS } from '../../services/leagueService';
import { useToast } from '../../context/ToastContext';

interface LeagueSelectorProps {
  allLeagues: LeagueDB[];
  selectedIds: string[];
  isLoading: boolean;
  isSaving: boolean;
  lang: Language;
  onToggle: (id: string) => void;
  onSave: () => void;
}

// 203(터키) 추가
const MAJOR_LEAGUE_IDS = ['39', '140', '135', '78', '61', '292', '88', '94', '203', '253', '307', '40', '98', '71', '262', '128', '144', '179'];
// 주요 컵 대회 ID 확장
const MAJOR_CUP_IDS = ['1', '2', '3', '848', '5', '9', '10', '11', '45', '48', '81', '143', '137', '66'];

export const LeagueSelector: React.FC<LeagueSelectorProps> = ({
  allLeagues, selectedIds, isLoading, isSaving, lang, onToggle, onSave
}) => {
  const [query, setQuery] = useState('');
  const [scoutedResults, setScoutedResults] = useState<LeagueDB[]>([]);
  const [manualLeagues, setManualLeagues] = useState<LeagueDB[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolvingExtras, setIsResolvingExtras] = useState(false);
  const debouncedQuery = useDebounce(query, 500);
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const resolveExistingExtras = async () => {
      if (selectedIds.length === 0 || allLeagues.length === 0) return;
      
      const mainIds = allLeagues.map(l => l.id.toUpperCase());
      const extraIds = selectedIds.filter(id => !mainIds.includes(id.toUpperCase()));
      
      if (extraIds.length > 0) {
        setIsResolvingExtras(true);
        try {
          const resolved = await fetchLeaguesByIds(extraIds);
          setManualLeagues(prev => {
            const pool = new Map<string, LeagueDB>();
            prev.forEach(l => pool.set(l.id, l));
            resolved.forEach(l => pool.set(l.id, l));
            return Array.from(pool.values());
          });
        } catch (e) { console.error(e); } finally { setIsResolvingExtras(false); }
      }
    };
    resolveExistingExtras();
  }, [allLeagues]);

  useEffect(() => {
    if (debouncedQuery.length >= 1) {
      setIsSearching(true);
      
      // 1. Search locally in LEAGUE_TRANSLATIONS for matching translations
      const q = debouncedQuery.toLowerCase();
      const localMatches = LEAGUE_TRANSLATIONS.filter(t => 
        Object.values(t.names).some(name => (name || '').toLowerCase().includes(q))
      );
      
      const localResults: LeagueDB[] = localMatches.map(m => {
        const existing = allLeagues.find(l => Number(l.id) === m.league_id) || manualLeagues.find(l => Number(l.id) === m.league_id);
        if (existing) return existing;
        return {
          id: m.league_id.toString(),
          name: m.names.en || 'Unknown League',
          logo_url: `https://media.api-sports.io/football/leagues/${m.league_id}.png`
        };
      });

      setScoutedResults(localResults);

      // 2. Call API for broader search
      searchLeagues(debouncedQuery).then(apiResults => {
        setScoutedResults(prev => {
          const combined = [...prev];
          if (Array.isArray(apiResults)) {
            apiResults.forEach(apiL => {
              if (!combined.find(l => l.id === apiL.id)) {
                combined.push(apiL);
              }
            });
          }
          return combined;
        });
        setIsSearching(false);
      }).catch(() => {
        setIsSearching(false);
      });
    } else {
      setScoutedResults([]);
    }
  }, [debouncedQuery, allLeagues, manualLeagues]);

  const handleLeagueClick = (league: LeagueDB) => {
    const isSelected = selectedIds.includes(league.id.toUpperCase());
    
    // 만약 선택되지 않은 상태에서 추가하려고 할 때만 제한 체크
    if (!isSelected && !isAdmin) {
      const maxLeagues = user ? 10 : 1;
      if (selectedIds.length >= maxLeagues) {
        showToast('info', user ? t(lang, 'maxLeaguesReached') : t(lang, 'maxLeaguesReachedGuest'));
        return;
      }
    }

    const isMain = allLeagues.find(l => l.id === league.id);
    if (!isMain) {
      setManualLeagues(prev => {
        if (prev.find(l => l.id === league.id)) return prev;
        return [...prev, league];
      });
    }
    onToggle(league.id);
  };

  const pool = useMemo(() => {
    const p = new Map<string, LeagueDB>();
    allLeagues.forEach(l => p.set(l.id, l));
    manualLeagues.forEach(l => p.set(l.id, l));
    return Array.from(p.values());
  }, [allLeagues, manualLeagues]);

  const majorLeagues = useMemo(() => pool.filter(l => MAJOR_LEAGUE_IDS.includes(l.id)), [pool]);
  const majorCups = useMemo(() => pool.filter(l => MAJOR_CUP_IDS.includes(l.id)), [pool]);
  const extraList = useMemo(() => pool.filter(l => !MAJOR_LEAGUE_IDS.includes(l.id) && !MAJOR_CUP_IDS.includes(l.id)), [pool]);

  const renderLeagueGrid = (list: LeagueDB[]) => (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-12 justify-items-center">
        {list.map(league => {
            const isSelected = selectedIds.includes(league.id.toUpperCase());
            const isExtra = !allLeagues.find(l => l.id === league.id);
            
            return (
                <div 
                    key={league.id} 
                    onClick={() => handleLeagueClick(league)}
                    className="flex flex-col items-center gap-3 cursor-pointer group w-full transition-all"
                >
                    <div className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 bg-white border-2 ${
                        isSelected 
                        ? 'border-rose-500 scale-110 shadow-xl shadow-rose-100' 
                        : 'border-slate-50 md:group-hover:border-slate-200 shadow-sm'
                    }`}>
                        <div className="w-10 h-10 sm:w-12 sm:h-12">
                            <img 
                                src={league.id === '1' ? "/assets/fiwc.png" : league.logo_url}
                                alt={league.name} 
                                className="w-full h-full object-contain" 
                            />
                        </div>
                        {isSelected && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-md animate-in zoom-in duration-300">
                                <Check size={14} strokeWidth={4} />
                            </div>
                        )}
                        {isExtra && !isSelected && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                <Sparkles size={10} fill="currentColor" />
                            </div>
                        )}
                    </div>
                    <span className={`text-[10px] font-black text-center uppercase tracking-tighter transition-colors w-full line-clamp-2 leading-tight px-1 ${isSelected ? 'text-rose-600' : 'text-slate-400 md:group-hover:text-slate-600'}`}>
                        {getLeagueName(Number(league.id), lang, league.name)}
                    </span>
                </div>
            );
        })}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-white font-sans animate-in fade-in duration-500">
      <div className="px-6 pt-4 mb-8">
        <div className="relative group">
          <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors">
            <Search size={22} strokeWidth={3} />
          </div>
          <input 
            type="text" 
            placeholder={t(lang, 'searchAllLeagues')}
            className="w-full bg-slate-50 border border-slate-100 rounded-[28px] py-4 pl-14 pr-12 text-base font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:bg-white transition-all placeholder:text-slate-300 shadow-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button 
              onClick={() => { setQuery(''); setScoutedResults([]); }}
              className="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-200 text-slate-500 rounded-full flex items-center justify-center md:hover:bg-slate-300 transition-all"
            >
              <X size={16} strokeWidth={3} />
            </button>
          )}
        </div>
        
        {(isSearching || isResolvingExtras) && (
          <div className="flex items-center gap-2 mt-4 px-2 animate-in slide-in-from-top-1">
            <Loader2 size={14} className="text-rose-500 animate-spin" />
            <span className="text-[11px] font-black text-rose-500 uppercase tracking-widest">
              {t(lang, 'scanningSectorData')}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 pb-40 custom-scrollbar">
        {isLoading ? (
          <div className="py-20 flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-rose-500" size={32} />
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">{t(lang, 'loadingLeagues')}</span>
          </div>
        ) : query.length >= 2 ? (
            <div className="animate-in fade-in duration-300">
                 <h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">{t(lang, 'searchResults')}</h3>
                 {scoutedResults.length > 0 ? renderLeagueGrid(
                   scoutedResults.filter(l => {
                     const q = query.toLowerCase();
                     const originalName = l.name.toLowerCase();
                     
                     // 1. Check current language translation
                     const currentLangName = getLeagueName(Number(l.id), lang, l.name).toLowerCase();
                     if (currentLangName.includes(q)) return true;
                     
                     // 2. Check original name
                     if (originalName.includes(q)) return true;

                     // 3. Check all other translations in LEAGUE_TRANSLATIONS
                     const translation = LEAGUE_TRANSLATIONS.find(t => t.league_id === Number(l.id));
                     if (translation) {
                       return Object.values(translation.names).some(name => name.toLowerCase().includes(q));
                     }
                     
                     return false;
                   })
                 ) : (
                    <div className="py-28 text-center bg-slate-50 rounded-[48px] border-2 border-dashed border-slate-100 flex flex-col items-center gap-6">
                        <Globe size={48} className="text-slate-200" />
                        <p className="text-[12px] font-black text-slate-400 uppercase tracking-[0.2em] px-10">{t(lang, 'noMatchingSectors')}</p>
                    </div>
                 )}
            </div>
        ) : (
          <div className="space-y-16">
             {majorLeagues.length > 0 && (
                 <section>
                    <h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8 flex items-center gap-2">
                        <div className="w-1 h-3 bg-rose-500 rounded-full"></div>
                        {t(lang, 'majorLeagues')}
                    </h3>
                    {renderLeagueGrid(majorLeagues)}
                 </section>
             )}

             {majorCups.length > 0 && (
                 <section>
                    <h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8 flex items-center gap-2">
                        <div className="w-1 h-3 bg-indigo-500 rounded-full"></div>
                        {t(lang, 'majorCups')}
                    </h3>
                    {renderLeagueGrid(majorCups)}
                 </section>
             )}

             {extraList.length > 0 && (
                 <section className="pb-10">
                    <h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8 flex items-center gap-2">
                        <div className="w-1 h-3 bg-emerald-500 rounded-full"></div>
                        {t(lang, 'addedSectors')}
                    </h3>
                    {renderLeagueGrid(extraList)}
                 </section>
             )}
          </div>
        )}
      </div>
    </div>
  );
};
